/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import DragNodeDataset from './dragnode';
import { pluck, pluckNumber, parseUnsafeString, getDashStyle, DASH_DEF,
  preDefStr, BLANKSTRING, hashify, dropHash, HASHSTRING, extend2,
  POSITION_CENTER, visibleStr, PXSTRING, setLineHeight, isIE
} from '@fusioncharts/core/src/lib';
import { addDep } from '@fusioncharts/core/src/dependency-manager';
import draggablelabelAnimation from './draggablelabel-animation';

let PX = preDefStr.PX,
  EVENTARGS = 'eventArgs',
  LABELDRAGEND = 'labeldragend',
  TRACKER_FILL = 'rgba(192,192,192,' + (isIE ? 0.002 : 0.000001) + ')'; // invisible but clickable

/**
 * Dataset class for draggable labels
 */
class DragNodeLabels extends DragNodeDataset {
  /**
   * Creates an instance of DragNodeLabels.
   * @memberof DragNodeLabels
   */
  constructor () {
    super();
    addDep({
      name: 'draggablelabelAnimation',
      type: 'animationRule',
      extension: draggablelabelAnimation
    });
  }
  /**
   * Sets the type of the component
   * @return {string} type
   */
  getType () {
    return 'dataset';
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'dragNodeLabels';
  }

  /**
    * Function for parsing all the attributes and value given by the user at chart and dataset level.
    * @param  {Object} datasetJSON JSON for dataset configurations
    * @return {boolean} if no datasetJSON is found
    */
  configure (datasetJSON) {
    if (datasetJSON) {
      this.trimData(datasetJSON);
      this.config.JSONData = datasetJSON.label;
    } else if (!datasetJSON && !this.config.JSONData) {
      return false;
    }

    var dataset = this,
      // chart = dataset.chart,
      // jsonData = chart.jsonData,
      chartAttr = dataset.getFromEnv('chart-attrib'),
      conf = dataset.config,
      setDataArr = dataset.config.JSONData || [],
      len = setDataArr.length,
      // dataStoreLen,
      dataStore = dataset.components.data,
      i;
    conf.viewMode = pluckNumber(chartAttr.viewmode, 0);
    if (!dataStore) {
      dataStore = dataset.components.data = [];
    }
    // dataStoreLen = dataStore.length;

    for (i = 0; i < len; i++) {
      dataset._setConfigure(i);
    }
  }

  /**
   * Function for parsing all the attributes and value given by the user at set level.
   * @param {number} index The data index for which the configuration is to be parsed.
   * @param {Object} updateObj It has the information of the data given by the user at the set level
   */
  _setConfigure (index, updateObj) {
    var dataset = this,
      setDataArr = dataset.config.JSONData,
      // chart = dataset.chart,
      setData = updateObj || setDataArr[index],
      dataStore = dataset.components.data,
      dataObj,
      text,
      // style = chart.config.style,
      inCanvasStyle = dataset.getFromEnv('style').inCanvasStyle,
      inCanFontSize = inCanvasStyle.fontSize,
      // labelColor,
      // labelBGColor,
      config,
      // labelBDColor,
      // alpha,
      labelFontSize;
    dataObj = dataStore[index];
    !dataObj && (dataObj = dataStore[index] = {});
    !dataObj.graphics && (dataObj.graphics = {});

    config = dataObj.config = dataObj.config || (dataObj.config = {});
    text = parseUnsafeString(pluck(setData.text, setData.label));
    config._options = setData;
    config.add = setData.add;
    if (text) {
      config.text = text;
      config.x = setData.x || 0;
      config.y = setData.y || 0;
      config.labelFontSize = labelFontSize = pluckNumber(setData.fontsize, inCanFontSize);

      config.labelColor = hashify(pluck(setData.color,
        inCanvasStyle.color));
      config.alpha = (pluckNumber(setData.alpha, 100)) / 100;
      config.allowDrag = pluckNumber(setData.allowdrag, 1);
      config.padding = pluckNumber(setData.padding, 5);

      // config.labelColor = labelColor = convertColor(pluck(setData.color, inCanvasStyle.color),
      //   pluckNumber(setData.alpha, 100));

      if (setData.fontsize) {
        config.labelCSS = {
          fontSize: labelFontSize + PX
        };
      } else {
        config.labelCSS = {};
      }

      config.labelBGColor = pluck(setData.bgcolor &&
        setData.bgcolor.replace(dropHash, HASHSTRING));
      config.labelBDColor = pluck(setData.bordercolor &&
        setData.bordercolor.replace(dropHash, HASHSTRING));
      config.link = setData.link;
      config.borderThickness = setData.borderthickness;
      config.dashLen = setData.dashlen;
      config.dashGap = setData.dashgap;
      config.dashed = setData.dashed;
      config.radius = setData.radius;
    }
  }

  /**
   * Get jsondata of dragable label dataset
   * @return {Object} The JSON data
   */
  getJSONData () {
    var dataset = this,
      dataStore = dataset.components.data,
      len = dataStore.length,
      jsonData = [],
      dataObj,
      i;
    for (i = 0; i < len; i++) {
      dataObj = dataStore[i];
      if (!dataObj.removed) {
        if (dataObj.config._options) {
          jsonData.push(dataObj.config._options);
        }
      }
    }
    return jsonData;
  }

  /**
   * Function to create container at the dataset level
   */
  createContainer () {
    var dataset = this,
      animationManager = dataset.getFromEnv('animationManager'),
      parentContainer = dataset.getLinkedParent().getChildContainer();

    !dataset.getContainer('dragLabelGroup') && dataset.addContainer('dragLabelGroup',
      animationManager.setAnimation({
        el: 'group',
        attr: {
          name: 'dragLabelGroup'
        },
        label: 'group',
        component: dataset,
        container: parentContainer.defaultVcanvasGroup
      }));
  }

  /**
   * Parses and sets the attributes during post space management
   *
   * @memberof DragNodeLabels
   */
  allocatePosition () {
    this.parsePlotAttributes();
  }

  /**
   * Parsing of attributes
   *
   * @memberof DragNodeLabels
   */
  parsePlotAttributes () {
    var dataset = this,
      dataStore = dataset.components.data,
      yAxis = dataset.getFromEnv('yAxis'),
      xAxis = dataset.getFromEnv('xAxis'),
      SmartLabel = dataset.getFromEnv('smartLabel'),
      style = dataset.getFromEnv('dataLabelStyle'),
      dashLen,
      dashGap,
      dashed,
      x,
      y,
      color,
      text,
      bgColor,
      borderColor,
      padding,
      config,
      attr,
      // alpha,
      dataObj,
      allowDrag,
      fontSize,
      radius,
      dim,
      borderThickness,
      len = dataStore && dataStore.length,
      lStyle,
      chartConfig = dataset.getFromEnv('chartConfig'),
      i,
      labelCSS;

    for (i = 0; i < len; i++) {
      dataObj = dataStore[i];
      if (dataObj.removed) {
        continue;
      }
      config = dataObj.config;
      !dataObj.graphics && (dataObj.graphics = {});
      config.index = i;
      x = xAxis.getPixel(config.x);
      y = yAxis.getPixel(config.y);
      text = config.text;
      bgColor = config.labelBGColor;
      borderColor = config.labelBDColor;
      padding = config.padding;
      allowDrag = config.allowDrag;
      fontSize = config.labelFontSize;
      color = config.labelColor;
      radius = config.radius;
      dashed = config.dashed;
      borderThickness = config.borderThickness;
      dashLen = config.dashLen;
      dashGap = config.dashGap;
      borderThickness = config.borderThickness;
      labelCSS = config.labelCSS;

      attr = {
        x: x,
        y: y,
        text: text,
        align: POSITION_CENTER,
        fill: color,
        'text-bound': [(bgColor || BLANKSTRING), (borderColor || BLANKSTRING),
          pluckNumber(borderThickness, 1),
          padding,
          pluckNumber(radius, 0),
          pluckNumber(dashed, 0)
            ? getDashStyle(pluckNumber(dashLen, 5),
              pluckNumber(dashGap, 4))
            : DASH_DEF
        ],
        visibility: visibleStr
      };

      lStyle = {
        backgroundColor: bgColor,
        borderColor: borderColor,
        borderPadding: padding,
        fontSize: fontSize + PXSTRING,
        fontStyle: style.fontStyle,
        fontWeight: style.fontWeight,
        borderRadius: 0,
        borderDash: DASH_DEF,
        fontFamily: style.fontFamily
      };
      setLineHeight(lStyle);
      SmartLabel.useEllipsesOnOverflow(chartConfig.useEllipsesWhenOverflow);
      SmartLabel.setStyle(lStyle);
      // Explicitly added for IE8. If lineHeight is added for any text it gets updated to all the spans. So to avoid that line-height
      // is applied.
      labelCSS['line-height'] = lStyle.lineHeight;
      config.eventArgs = {
        link: config.link,
        text: text,
        x: x,
        y: y,
        allowdrag: allowDrag,
        sourceType: 'labelnode'
      };

      config.props = {
        element: {
          attr: attr
        }
      };

      config.labelCSSApplied = labelCSS;

      dim = SmartLabel.getOriSize(text);
      config.width = dim.width;
      config.height = dim.height;
      config.xPos = x;
      config.yPos = y;
    }
  }
  /**
   * Function ot draw the entire dataset
   */
  draw () {
    var dataset = this,
      dataStore = dataset.components.data,
      animationManager = dataset.getFromEnv('animationManager'),
      style = dataset.getFromEnv('dataLabelStyle'),
      config,
      dataObj,
      element,
      elementCheck,
      len = dataStore && dataStore.length,
      removeDataArr = dataset.components.removeDataArr || [],
      removeDataArrLen = removeDataArr.length,
      i,
      dataLabelContainer,
      labelCSS;

    dataset.createContainer();
    dataLabelContainer = dataset.getContainer('dragLabelGroup');
    dataLabelContainer.css({
      'font-weight': style.fontWeight,
      'font-style': style.fontStyle,
      'font-size': style.fontSize,
      'font-family': style.fontFamily
    });

    for (i = 0; i < len; i++) {
      dataObj = dataStore[i];
      if (dataObj.removed) {
        continue;
      }
      config = dataObj.config;
      elementCheck = dataObj.graphics.element;

      element = animationManager.setAnimation({
        el: elementCheck || 'text',
        container: dataLabelContainer,
        css: config.labelCSS,
        attr: config.props.element.attr,
        component: dataset
      });

      if (!elementCheck) {
        dataObj.graphics.element = element;
      } else {
        // If label css was applied on this element and now label css is not given then remove css
        if (config.labelCSSApplied && !labelCSS) {
          element.removeCSS();
        }
        element.show()
          .css(config.labelCSS);
      }

      element.data('eventArgs', config.eventArgs);
    }
    dataset.drawTracker();
    for (i = 0; i < removeDataArrLen; i++) {
      dataset._removeDataVisuals(removeDataArr.shift());
    }
  }

  /**
   * Function ot draw the tracker over the lables for showing the tooltips and other mouse interactivity
   */
  drawTracker () {
    var dataset = this,
      dataStore = dataset.components.data,
      animationManager = dataset.getFromEnv('animationManager'),
      chart = dataset.getFromEnv('chart'),
      manager = dataset.getLinkedParent(),
      conf = dataset.config,
      trackerGroup = dataset.getContainer('dragLabelGroup'),
      len = dataStore && dataStore.length,
      dataObj,
      config,
      padding,
      elemMouseDownFN = function () { // Long press eve
        var ele = this,
          data = ele.data('drag-options'),
          dataSet = data.dataset,
          index = data.index,
          labelObj = dataSet.components.data[index];
        ele.data('fire_click_event', 1);
        manager.clearLongPress();
        manager.triggerLabelUI(ele, labelObj);
      },
      elemMouseMoveFN = function () {
        // Whether to fire the click event ot not
        var ele = this;
        if (ele.data('fire_click_event')) {
          ele.data('fire_click_event', 0);
          manager.clearLongPress();
        }
      },
      elemMouseUPFN = function (data) {
        var ele = this,
          fireClick = ele.data('fire_click_event');
        manager.clearLongPress();
        if (fireClick) {
        /**
           *
           * > Applicable to `dragnode` chart only.
           *
           * @event FusionCharts#labelClick
           * @group chart-powercharts:dragnode
           *
           * @param {number} chartX - x-coordinate of the pointer relative to the chart.
           * @param {number} chartY - y-coordinate of the pointer relative to the chart.
           * @param {number} pageX - x-coordinate of the pointer relative to the page.
           * @param {number} pageY - y-coordinate of the pointer relative to the page.
           *
           * @param {number} x - The x-value of the label node scaled as per the axis of the chart.
           * @param {number} y - The y-value of the label node scaled as per the axis of the chart.
           *
           * @param {string} text - The text value of the label.
           */
          chart.plotEventHandler(ele, data, 'LabelClick');
        }
      },
      elemHoverFN = function (data) {
        var ele = this;
        /**
         *
         * > Applicable to `dragnode` chart only.
         *
         * @event FusionCharts#labelRollOver
         * @group chart-powercharts:dragnode
         *
         * @param {number} chartX - x-coordinate of the pointer relative to the chart.
         * @param {number} chartY - y-coordinate of the pointer relative to the chart.
         * @param {number} pageX - x-coordinate of the pointer relative to the page.
         * @param {number} pageY - y-coordinate of the pointer relative to the page.
         *
         * @param {number} x - The x-value of the label node scaled as per the axis of the chart.
         * @param {number} y - The y-value of the label node scaled as per the axis of the chart.
         *
         * @param {string} text - The text value of the label.
         */
        chart.plotEventHandler(ele, data, 'LabelRollover');
      },
      elemOutFN = function (data) {
        var ele = this;
        /**
         *
         * > Applicable to `dragnode` chart only.
         *
         * @event FusionCharts#labelRollOut
         * @group chart-powercharts:dragnode
         *
         * @param {number} chartX - x-coordinate of the pointer relative to the chart.
         * @param {number} chartY - y-coordinate of the pointer relative to the chart.
         * @param {number} pageX - x-coordinate of the pointer relative to the page.
         * @param {number} pageY - y-coordinate of the pointer relative to the page.
         *
         * @param {number} x - The x-value of the label node scaled as per the axis of the chart.
         * @param {number} y - The y-value of the label node scaled as per the axis of the chart.
         *
         * @param {string} text - The text value of the label.
         */
        chart.plotEventHandler(ele, data, 'LabelRollout');
      },
      eventArgs,
      attr,
      allowDrag,
      text,
      trackerElement,
      trackerElementCheck,
      x,
      y,
      width,
      height,
      dragMove = function (event, data) {
        var ele = this;
        dataset._labelDragMove.call(ele, event, data, chart);
      },
      dragStart = function (event) {
        var ele = this;
        dataset._labelDragStart.call(ele, event, chart);
      },
      dragUp = function (event) {
        var ele = this;
        dataset._labelDragUp.call(ele, event);
      },
      i;

    for (i = 0; i < len; i++) {
      dataObj = dataStore[i];
      if (dataObj.removed) {
        continue;
      }
      config = dataObj.config;
      padding = config.padding || 0;
      width = config.width;
      height = config.height;
      x = config.xPos - width / 2;
      y = config.yPos - height / 2;
      allowDrag = config.allowDrag;
      text = config.text;
      trackerElementCheck = dataObj.graphics.trackerElement;
      attr = {
        x: x - padding,
        y: y - padding,
        width: width + padding * 2,
        height: height + padding * 2,
        cursor: config.allowDrag ? 'move' : BLANKSTRING,
        fill: TRACKER_FILL,
        stroke: TRACKER_FILL
      };
      eventArgs = {
        link: config.link,
        text: text,
        x: x,
        y: y,
        allowdrag: allowDrag,
        sourceType: 'labelnode'
      };
      trackerElement = animationManager.setAnimation({
        el: trackerElementCheck || 'rect',
        container: trackerGroup,
        attr: attr,
        component: dataset
      });
      if (!trackerElementCheck) {
        dataObj.graphics.trackerElement = trackerElement;
        trackerElement.on('fc-mousedown', elemMouseDownFN)
          .on('fc-mousemove', elemMouseMoveFN)
          .on('fc-mouseup', elemMouseUPFN)
          .data('viewMode', conf.viewMode)
          .data(preDefStr.EVENTARGS, eventArgs)
          .hover(elemHoverFN, elemOutFN);
        config.allowDrag && trackerElement.drag(dragMove, dragStart, dragUp);
      }
      trackerElement.data('drag-options', {
        index: i,
        dataset: dataset
      });
    }
  }

  /**
   * Callback function when the dragging of the label starts
   */
  _labelDragStart () {
    var ele = this,
      bBox = ele.getBBox(),
      data = ele.data('drag-options'),
      dataset = data.dataset,
      manager = dataset.getLinkedParent(),
      index = data.index,
      labelObj = dataset.components.data[index],
      labelElement = labelObj.graphics.element,
      dragStart = labelObj.dragStart = labelObj.dragStart || (labelObj.dragStart = {});
    data.ox = labelElement.attr('x');
    data.oy = labelElement.attr('y');
    data.bBox = bBox;
    dragStart.xPos = labelObj.config.xPos;
    dragStart.yPos = labelObj.config.yPos;
    dragStart.bBox = bBox;
    ele.data('fire_click_event', 1);
    ele.data('fire_dragend', 0);
    manager.clearLongPress();
    manager.triggerLabelUI(ele, labelObj);
  }

  /**
   * Callback function when the labels are dragged around
   * @param {Event} event The actual mouse event
   * @param {Object} positionData Contains the coordinates of the label elements while they are dragged
   */
  _labelDragMove (event, positionData) { // eslint-disable-line no-unused-vars
    var ele = this,
      data = ele.data('drag-options'),
      index = data.index,
      dataset = data.dataset,
      chart = dataset.getFromEnv('chart'),
      chartConfig = dataset.getFromEnv('chartConfig'),
      canvasLeft = chartConfig.canvasLeft,
      canvasRight = chartConfig.canvasRight,
      canvasBottom = chartConfig.canvasBottom,
      canvasTop = chartConfig.canvasTop,
      manager = dataset.getLinkedParent(),
      labelObj = dataset.components.data[index],
      labelElement = labelObj.graphics.element,
      dragStart = labelObj.dragStart,
      bBox = dragStart.bBox,
      dx = positionData[0],
      dy = positionData[1],
      startX = dragStart.bBox.x + dx,
      endX = dragStart.bBox.x2 + dx,
      startY = dragStart.bBox.y + dy,
      endY = dragStart.bBox.y2 + dy,
      yAxis = dataset.getFromEnv('yAxis'),
      xAxis = dataset.getFromEnv('xAxis'),
      xPos,
      yPos;
    // Bound limits
    if (startX < canvasLeft) {
      dx += (canvasLeft - startX);
    }
    if (endX > canvasRight) {
      dx -= (endX - canvasRight);
    }
    if (startY < canvasTop) {
      dy += (canvasTop - startY);
    }
    if (endY > canvasBottom) {
      dy -= (endY - canvasBottom);
    }
    dragStart.draged = true;
    ele.attr({
      x: bBox.x + dx,
      y: bBox.y + dy
    });
    xPos = data.ox + dx;
    yPos = data.oy + dy;
    labelElement.attr({
      x: data.ox + dx,
      y: data.oy + dy
    });
    labelObj.config.x = xAxis.getValue(xPos);
    labelObj.config.y = yAxis.getValue(yPos);
    if (!ele.data('fire_dragend')) {
    /**
       *
       * > Applicable to `dragnode` chart only.
       *
       * @event FusionCharts#labelDragStart
       * @group chart-powercharts:dragnode
       *
       * @param {number} chartX - x-coordinate of the pointer relative to the chart.
       * @param {number} chartY - y-coordinate of the pointer relative to the chart.
       * @param {number} pageX - x-coordinate of the pointer relative to the page.
       * @param {number} pageY - y-coordinate of the pointer relative to the page.
       *
       * @param {number} x - The x-value of the label node scaled as per the axis of the chart.
       * @param {number} y - The y-value of the label node scaled as per the axis of the chart.
       *
       * @param {string} text - The text value of the label.
       */
      chart.plotEventHandler(ele, event, 'LabelDragStart');
      ele.data('fire_dragend', 1);
    }
    if (ele.data('fire_click_event')) {
      ele.data('fire_click_event', 0);
      manager.clearLongPress();
    }
  }

  /**
   * Callback function when dragging is stopped over a label
   * @param {Event} event The actual event object
   */
  _labelDragUp (event) {
    var ele = this,
      data = ele.data('drag-options'),
      index = data.index,
      dataset = data.dataset,
      chart = dataset.getFromEnv('chart'),
      xAxis = chart.getChildren('xAxis')[0],
      yAxis = chart.getChildren('yAxis')[0],
      manager = dataset.getLinkedParent(),
      sourceEvent = LABELDRAGEND,
      labelObj = dataset.components.data[index],
      dragStart = labelObj.dragStart,
      eventArgs = ele.data(EVENTARGS);

    eventArgs.x = xAxis.getValue(ele.attr('x'));
    eventArgs.y = yAxis.getValue(ele.attr('y'));

    dragStart.draged = false;
    if (ele.data('fire_dragend')) {
    // eventCord = getMouseCoordinate(chart.linkedItems.container, event);
    // eventCord.sourceEvent = sourceEvent;

      // Fire the ChartUpdated event
      chart.fireChartInstanceEvent('chartupdated', extend2({
        sourceEvent: sourceEvent
      }, eventArgs), event);
      chart.fireChartInstanceEvent('chartupdated', eventArgs, event);
      /**
       *
       * > Applicable to `dragnode` chart only.
       *
       * @event FusionCharts#labelDragEnd
       * @group chart-powercharts:dragnode
       *
       * @param {number} chartX - x-coordinate of the pointer relative to the chart.
       * @param {number} chartY - y-coordinate of the pointer relative to the chart.
       * @param {number} pageX - x-coordinate of the pointer relative to the page.
       * @param {number} pageY - y-coordinate of the pointer relative to the page.
       *
       * @param {number} x - The x-value of the label node scaled as per the axis of the chart.
       * @param {number} y - The y-value of the label node scaled as per the axis of the chart.
       *
       * @param {string} text - The text value of the label.
       */
      chart.plotEventHandler(ele, event, sourceEvent);
    }
    manager.clearLongPress();
  }

  /**
   * Function to remove data during update if number of data has reduced than the previous render
   * @param {number} index The data index from which data has to be removed.
   * @param {number} stretch The index upto which data has to be removed
   */
  removeData (index, stretch) {
    var dataset = this,
      components = dataset.components,
      dataStore = components.data;
    components.removeDataArr = dataStore.splice(index, stretch);
  }

  /**
   * function to Remove data plots if the number of current data plots/categories
   * is more than the existing ones.
   * @param  {Object} datasetJSON JSON for dataset configurations
   */
  trimData (datasetJSON) {
    if (!this.config.JSONData) {
      return;
    }
    let dataSet = this,
      prevData = dataSet.config.JSONData,
      prevDataLength = prevData && prevData.length,
      currDataLength = (datasetJSON.label && datasetJSON.label.length) || 0,
      dataDiff = prevDataLength - currDataLength;

    // Removing data plots if the number of current data plots/categories
    // is more than the existing ones.
    if (dataDiff > 0) {
      dataSet.removeData(currDataLength, dataDiff);
    }
  }
}
export default DragNodeLabels;
